import type {
  NormalisedRepeatInterval,
  NormalisedRequest,
} from '@onhandtech/onhand-fe-shared/lib/api/v4/requests/requests.types'
import type {
  PatchRequestPayload,
  RequestRejectionReason,
} from '@onhandtech/onhand-fe-shared/lib/contracts/api/v4/requests/requests'

type HeroPointsParam = {
  hero_points: number
}

export const getHeroPointsProp = (
  existingHeroPoints: number,
  updatedHeroPoints: number,
): Partial<HeroPointsParam> => ({
  ...(updatedHeroPoints === existingHeroPoints ? {} : { hero_points: updatedHeroPoints }),
})

type RejectionParam = {
  rejection_reason_enum: RequestRejectionReason
  rejection_description: string
}

export const getRejectionPayload = (
  reason?: RequestRejectionReason,
  reasonDescription?: string | null,
): Partial<RejectionParam> =>
  reason
    ? {
        rejection_reason_enum: reason,
        ...(reasonDescription ? { rejection_description: reasonDescription } : {}),
      }
    : {}

type RequestIntervalPayload = {
  repeat_interval: 'ONE_OFF' | 'WEEKLY'
}

export const getRepeatIntervalPayload = (
  existingRepeatInterval: NormalisedRepeatInterval,
  draftRepeatInterval: NormalisedRepeatInterval,
): Partial<RequestIntervalPayload> => {
  if (existingRepeatInterval !== draftRepeatInterval) {
    return {
      repeat_interval: draftRepeatInterval === 'REGULAR' ? 'WEEKLY' : 'ONE_OFF',
    }
  }

  return {}
}

type ManualCharityPayload = {
  beneficiary_url: string | null
  beneficiary_name: string | null
}

export const getManualBeneficiaryDetailsPayload = (
  existingManaualBeneficiaryDetails:
    | {
        name: string
        website: string
      }
    | undefined,
  beneficiaryVolunteerGroupId: string | undefined,
): Partial<ManualCharityPayload> => {
  if (!existingManaualBeneficiaryDetails && !beneficiaryVolunteerGroupId) {
    return {}
  }

  return beneficiaryVolunteerGroupId
    ? { beneficiary_name: null, beneficiary_url: null }
    : {
        beneficiary_name: existingManaualBeneficiaryDetails?.name,
        beneficiary_url: existingManaualBeneficiaryDetails?.website,
      }
}

type PatchRequestKey = keyof Omit<
  PatchRequestPayload,
  | 'hero_points'
  | 'approved'
  | 'rejection_description'
  | 'rejection_reason_enum'
  | 'permission_assignments'
  | 'hidden'
  | 'is_group_mission'
  | 'active'
  | 'max_enrollment_count'
  | 'max_enrollments_open_or_completed'
  | 'address'
  | 'cover_image_files'
>

export const getRequestFieldProp = (
  paramKey: PatchRequestKey,
  existingValue: string,
  newValue: string,
): Partial<{ [key in PatchRequestKey]: string }> => ({
  ...(existingValue === newValue ? {} : { [paramKey]: newValue }),
})

export const getRequestPayload = (
  existingRequest: NormalisedRequest,
  draftRequest: NormalisedRequest,
): Omit<PatchRequestPayload, 'cover_image_files'> => ({
  ...getHeroPointsProp(existingRequest.heroPoints, draftRequest.heroPoints),
  ...getRepeatIntervalPayload(existingRequest.repeatInterval, draftRequest.repeatInterval),
  ...getRequestFieldProp('description', existingRequest.description, draftRequest.description),
  ...getRequestFieldProp(
    'task_type_value',
    existingRequest.taskType?.value as string,
    draftRequest.taskType?.value as string,
  ),
  ...getRequestFieldProp(
    'beneficiary_volunteer_group_id',
    existingRequest.beneficiary?.id ?? '',
    draftRequest.beneficiary?.id ?? '',
  ),
  ...getManualBeneficiaryDetailsPayload(
    existingRequest.manualBeneficiary,
    draftRequest.beneficiary?.id,
  ),
  ...getRejectionPayload(draftRequest.rejection?.type, draftRequest.rejection?.description),
  active: true,
  max_enrollment_count: 1,
})
